import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { CheckCircle, Mail, FileText, ExternalLink, User, Users, Info } from 'lucide-react';
import { useFormStore } from '../store/formStore';

export function SuccessStep() {
  const { apiResponse } = useFormStore();

  const isRefused = apiResponse?.DemandStatus === 'ETR';

  useEffect(() => {
    localStorage.removeItem('yelloan-credit-form');
  }, []);

  if (isRefused) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Info className="w-20 h-20 text-primary-400" strokeWidth={1.5} />
            </div>
            <CardTitle className="text-2xl text-neutral-800">
              Nous n'avons pas pu trouver d'offre adaptée pour le moment
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="text-center space-y-4">
              <p className="text-neutral-700">
                Après avoir interrogé l'ensemble de nos partenaires bancaires, aucun d'entre eux n'est malheureusement en mesure de financer votre projet aujourd'hui.
              </p>
              <p className="text-sm text-neutral-500">
                Nous savons que c'est une déception. Sachez que les banques appliquent des critères très stricts à l'instant T. Cette décision ne juge en rien votre situation personnelle.
              </p>
            </div>

            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 sm:p-6 flex items-start gap-3">
              <Mail className="w-5 h-5 flex-shrink-0 mt-0.5 text-primary-500" />
              <div className="text-sm">
                <p className="font-medium text-primary-900">Vous avez reçu un email</p>
                <p className="text-primary-700 mt-1">
                  Ne vous découragez pas ! Nous vous avons envoyé des conseils (modifier le montant, emprunter à deux…) pour vous aider à ajuster votre demande.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle className="w-20 h-20 text-success-500" strokeWidth={1.5} />
          </div>
          <CardTitle className="text-2xl text-success-700">
            Votre demande a été acceptée !
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Message principal */}
          <div className="text-center space-y-3">
            <p className="text-lg text-neutral-700">
              Notre partenaire a accepté de financer votre projet.
            </p>
            <p className="text-neutral-600">
              Finalisez votre dossier en signant électroniquement vos documents.
            </p>
          </div>

          {/* Cartes de signature */}
          {apiResponse?.BorrowerUrl && (
            <div className="space-y-4">
              {/* Carte pour l'emprunteur principal */}
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 border-2 border-primary-300 rounded-lg p-4 sm:p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="bg-primary-600 text-white p-3 rounded-full">
                    <User className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary-900 text-lg">
                      Emprunteur principal
                    </h3>
                    <p className="text-sm text-primary-700">
                      Cliquez pour accéder à votre espace de signature
                    </p>
                  </div>
                </div>
                <a
                  href={apiResponse.BorrowerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors shadow-md hover:shadow-lg"
                >
                  <span className="whitespace-nowrap">Finaliser ma demande</span>
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>

              {/* Carte pour le co-emprunteur si présent */}
              {apiResponse.CoBorrowerUrl && (
                <div className="bg-gradient-to-br from-success-50 to-success-100 border-2 border-success-200 rounded-lg p-4 sm:p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-success-600 text-white p-3 rounded-full">
                      <Users className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-success-700 text-lg">
                        Co-emprunteur
                      </h3>
                      <p className="text-sm text-success-700">
                        Cliquez pour accéder à votre espace de signature
                      </p>
                    </div>
                  </div>
                  <a
                    href={apiResponse.CoBorrowerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-success-600 text-white font-semibold rounded-lg hover:bg-success-700 transition-colors shadow-md hover:shadow-lg"
                  >
                    <span className="whitespace-nowrap">Finaliser ma demande</span>
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Informations complémentaires */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 sm:p-6 space-y-4">
            <h3 className="font-semibold text-primary-900 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Informations importantes
            </h3>

            <div className="space-y-3 text-sm text-primary-800">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Signature électronique</p>
                  <p className="text-primary-700">
                    Utilisez les liens ci-dessus pour signer vos documents en ligne de manière sécurisée.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Email de confirmation</p>
                  <p className="text-primary-700">
                    Vous recevrez également un email avec tous les liens et informations nécessaires.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Documents requis</p>
                  <p className="text-primary-700">
                    Vous trouverez la liste des documents à fournir dans votre espace de signature.
                  </p>
                </div>
              </div>
            </div>
          </div>


        </CardContent>
      </Card>
    </div>
  );
}

